import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { BugReportModal } from '@/components/BugReportModal';

export function UserMenu() {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [isOpen, setIsOpen] = useState(false);
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!user) return null;

  // Generate initials for fallback avatar
  const initials = user.username
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <>
      <div className="relative" ref={menuRef}>
        {/* Avatar button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 p-1 rounded-full hover:bg-wall-border/50 transition-colors"
        >
          {user.profilePicture ? (
            <img
              src={user.profilePicture}
              alt={user.username}
              className="w-9 h-9 rounded-full object-cover border-2 border-wall-accent/50"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-linear-to-br from-wall-accent to-wall-accent-secondary flex items-center justify-center text-sm font-bold text-white">
              {initials}
            </div>
          )}
          <svg
            className={`w-4 h-4 text-wall-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-wall-surface/95 backdrop-blur-md border border-wall-border/50 rounded-xl shadow-2xl overflow-hidden z-50">
            {/* User info header */}
            <div className="px-4 py-3 border-b border-wall-border/30">
              <p className="text-sm font-semibold text-wall-text truncate">{user.username}</p>
              <p className="text-xs text-wall-muted truncate">{user.email}</p>
              {user.adminRole && (
                <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-wall-accent/20 text-wall-accent rounded-full">
                  {user.adminRole}
                </span>
              )}
            </div>

            {/* Menu items */}
            <div className="py-2">
              <Link
                to="/profile"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-sm text-wall-text hover:bg-wall-border/30 transition-colors"
              >
                <svg
                  className="w-4 h-4 text-wall-muted"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                Profile
              </Link>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setIsBugReportOpen(true);
                }}
                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-wall-text hover:bg-wall-border/30 transition-colors"
              >
                <svg
                  className="w-4 h-4 shrink-0 text-wall-muted"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth={2} />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="m3 7 9 6 9-6"
                  />
                </svg>
                Report a bug
              </button>
              {user.adminRole && ['SUPER_ADMIN', 'ADMIN'].includes(user.adminRole) && (
                <>
                  <Link
                    to="/admin-panel/reports"
                    onClick={() => {
                      setIsOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2 text-sm text-wall-text hover:bg-wall-border/30 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-wall-muted"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h4m5 4H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7l5 5v9a2 2 0 0 1-2 2Z"
                      />
                    </svg>
                    Bug Reports
                  </Link>
                  <Link
                    to="/admin-panel/users"
                    onClick={() => {
                      setIsOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2 text-sm text-wall-text hover:bg-wall-border/30 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-wall-muted"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Users
                  </Link>
                </>
              )}
              <button
                onClick={() => {
                  setIsOpen(false);
                  logout();
                }}
                className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
      <BugReportModal isOpen={isBugReportOpen} onClose={() => setIsBugReportOpen(false)} />
    </>
  );
}
